/**
 * Forward brand palette — single source of truth.
 *
 * See docs/design/forward-brand-system.md for the full brand system. This module holds the
 * hue ramps and the chrome ROLE tokens the app paints with. Consumers reference roles
 * (`BRAND.depth`, `BRAND.highlight`) rather than raw hex, so the identity can evolve in one
 * place. `RF_BRAND` / `CHART_COLORS` in `lib/constants.ts` are derived from these ramps.
 *
 * Two-tone accent rule (the core discipline):
 *   - action  (Iris)       — white text, AA — buttons/links/focus/active-nav (wired via
 *                            Cloudscape applyTheme in Phase 2; not yet used by the chrome here)
 *   - highlight (Cornflower)— dark text only — accent bar, emphasis fills
 *   - depth   (Dark Violet) — brand-depth band — header/footer/hero surfaces
 */

// --- Hue ramps ------------------------------------------------------------

/** Iris — Forward signature / action accent. (Identical to Yield's `violet` chart family.) */
export const iris = {
  50: "#eeecfb",
  100: "#dedbf5",
  200: "#c3bdef",
  300: "#a49bec",
  400: "#7d6fe0",
  500: "#5c4fd6",
  600: "#4a3db8",
  700: "#3829b0",
  800: "#2f2492",
  900: "#241c75",
} as const;

/** Dark Violet — brand-depth anchor. */
export const violet = {
  600: "#5d4180",
  700: "#4a3268",
  800: "#382652",
  900: "#2f1f47",
  950: "#241536",
} as const;

/** Cornflower — highlight / brand-fill. Dark text only — never white text. */
export const cornflower = {
  300: "#fdd9a2",
  400: "#fcc97a",
  500: "#fbb150",
  600: "#e09a3a",
  700: "#b8792a",
} as const;

/** Secondary data-viz hues (RF secondaries). Charts only — not for status chrome. */
export const secondary = {
  mayaBlue: "#70C9FF",
  lightGreen: "#7ADE91",
  magenta: "#BF3885",
  imperialRed: "#F2454D",
} as const;

/** Near-black ink for body text on light/highlight surfaces. */
export const ink = "#1A1A1A";
/** Deepest violet — retained as a distinct data-viz tone (RF_BRAND.darkPurple). */
export const darkPurple = "#261736";

// --- Chrome role tokens ---------------------------------------------------

/**
 * The role tokens the app chrome paints with. Currently mapped to the established
 * violet/cornflower identity (no visual change vs the pre-refresh hardcoded hex); Phase 2
 * introduces the Iris action accent via Cloudscape applyTheme.
 */
export const BRAND = {
  /** Brand-depth band — header, footer badge, login/hero backgrounds. */
  depth: violet[800],
  /** Text on `depth`. */
  onDepth: "#ffffff",
  /** Highlight / brand-fill — accent bar, emphasis fills. Pair with dark text only. */
  highlight: cornflower[500],
  /** Body ink — text on light/highlight surfaces, and the banner's inner control fill. */
  ink,
  /** Iris action accent — reserved for the Phase 2 applyTheme recolour. */
  action: iris[500],
} as const;
